'use client';

import { selectedCurrencyAtom, userAtom } from '@/store';
import { balanceListAtom, setBalanceListAtom, rechargeNotificationAtom, RechargeNotification, balancePollingPausedAtom } from '@/store/balance';
import { cn } from "@/utils/classnames";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import walletIcon from "@/features/wallet/assets/icon-wallet.png";
// import bonusIcon from '@/features/wallet/assets/bonus.png';
import { useTranslations } from 'next-intl';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { usePathname } from '@/i18n/navigation';
import { showWalletModal, WalletEntrance } from '@/features/wallet/components/wallet-modal';
import { showConfirmDialog } from '@/components/ui/confirm-dialog';
import Image from 'next/image';
import { toNum, add, sub, toDP, ROUND_DOWN } from '@/libs/decimal';
import { useGetStaticResource, useGetCurrencyIcon } from '@/services/static-resource';
import { inPlayPages } from '@/const/page-url';
import { useFormatBalanceDisplay, useBalanceFormatter } from '@/hooks/use-balance-formatter';
import { RechargeAnimation } from '@/app/[locale]/(main)/(non-activity)/components/balance-bar/recharge-animation';
import { getCurrency } from '@/utils/currency';
import { useGetBalanceList } from '@/services/balance-list';
import { CURRENCY_MAP_ENTRANCE, DEPOSIT_TYPE_MAP } from '@/const/games';
import { AudioId, useAudioHelper } from '@/utils/audio-hepler';
import { isRummyActiveAtom } from '../../rummy/store';
import { useAudioManager } from '@/hooks/use-audio-manager';
import styles from './index.module.css';
import { track } from '@/utils/track';
import { getParents, isTouchSupported } from '@/utils/node';
import { showModalSecondConfirm } from './second-confirm';
import { isRummyInGameAtom } from '@/app/[locale]/(main)/(non-activity)/rummy/store';
import { isCasinoInGameAtom } from '@/store/casino';
import { isInKbFruitGameAotm } from '@/app/[locale]/(main)/(non-activity)/casino/store';
import { isFantasyActiveAtom } from '@/app/[locale]/(main)/(non-activity)/fantasy/store';
import { useChangeSubscription } from '@/hooks/use-change-subscription';
import IconFont from '@/components/ui/iconFont';
import { isSportsActiveAtom } from '../../sports/store';

interface BalanceItemProps {
  currency: string;
  balance: string;
  bonus: string;
  isSelected: boolean;
  onSelectAction: () => void;
  getCurrencyIcon: (currency: string, staticResource: any) => string | null;
  t: any;
}

// 余额列表项组件
function BalanceItem({
  currency,
  balance,
  bonus,
  isSelected,
  onSelectAction,
  getCurrencyIcon,
  t,
}: BalanceItemProps) {
  const { data: staticResource } = useGetStaticResource();
  const { formatBalanceDetail } = useBalanceFormatter();
  const tPromotions = useTranslations('personal');

  // 获取货币图标
  const currencyIcon = getCurrencyIcon(currency, staticResource);

  // 判断是否显示 bonus
  const shouldShowBonus = bonus && bonus !== '0' && bonus !== '';

  // 处理 notice 图标点击
  const handleNoticeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    showConfirmDialog({
      title: t('bonusDescription'),
      content: t('unlockBonusAfterTurnover'),
      confirmText: t('ok'),
      hideCancelButton: true,
    });
  };

  return (
    <div
      className={cn(
        "h-[45px] rounded-[8px] flex items-center px-[6px] cursor-pointer transition-colors",
        isSelected ? "bg-selected" : ""
      )}
      onClick={onSelectAction}
    >
      {/* 货币图标和名称 */}
      <div className="relative flex items-center flex-1">
        <div className="w-4 h-4 mr-[5px] flex-shrink-0">
          {currencyIcon && (
            <Image
              src={currencyIcon}
              alt={currency}
              width={16}
              height={16}
              className="w-full h-full object-contain"
            />
          )}
        </div>
        <span className={cn(
          "text-[11px] font-semibold",
          isSelected ? "text-white" : "text-[#929EB2]"
        )}>
          {currency}
        </span>
      </div>

      {/* 余额和 bonus 显示 */}
      <div className="flex flex-col items-end py-[6px]">
        {/* 余额显示 */}
        <span className={cn(
          "text-[11px] font-semibold leading-none",
          shouldShowBonus && "mb-[7px]",
          "text-white",
        )}>
          {(() => {
            const info = staticResource?.currency?.[currency as keyof typeof staticResource.currency] as any;
            const isFiat = Boolean(info?.region);
            const symbol = info?.symbol ?? getCurrency(currency);
            const balanceNum = toNum(balance);
            let numStr = formatBalanceDetail(toNum(balance), currency);

            // 如果非USDT 小于 0.01 且大于 0，显示 <0.01
            if (currency !== 'USDT' && balanceNum > 0 && balanceNum < 0.01) {
              numStr = '<0.01';
            } else if (currency === 'USDT' && balanceNum > 0 && balanceNum < 0.0001) {
              // 小于 0.0001 且大于 0，显示 <0.0001
              numStr = '<0.0001';
            }
            return (isFiat && symbol ? symbol : '') + numStr;
          })()}
        </span>

        {/* Bonus 显示 */}
        {shouldShowBonus && (
          <div className="flex items-center">
            <IconFont
              name="tips"
              size={12}
              color="#a9b5cb"
              className="cursor-pointer mr-[6px] active:scale-90"
              onClick={handleNoticeClick}
            />
            <span className='mr-[4px] text-white font-semibold text-[9px] px-[3px] h-[10px] bg-[#ed8e00] leading-[9px] text-center rounded-[2px]'>
              {tPromotions('history.bonus')}
            </span>
            <span className="font-medium text-accent-yellow leading-none text-[11px]">
              {(() => {
                const info = staticResource?.currency?.[currency as keyof typeof staticResource.currency] as any;
                const isFiat = Boolean(info?.region);
                const symbol = info?.symbol ?? getCurrency(currency);
                const bonusNum = toNum(bonus);

                // 如果 bonus 小于 0.01 且大于 0，显示 <0.01
                if (currency === 'USDT' && bonusNum > 0 && bonusNum < 0.0001) {
                  return (isFiat && symbol ? symbol : '') + '<0.0001';
                }

                // 如果非USDT bonus 小于 0.01 且大于 0，显示 <0.01
                if (currency !== 'USDT' && bonusNum > 0 && bonusNum < 0.01) {
                  return (isFiat && symbol ? symbol : '') + '<0.01';
                }

                const numStr = formatBalanceDetail(bonusNum, currency);
                return (isFiat && symbol ? symbol : '') + numStr;
              })()}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function BalanceBar() {
  const t = useTranslations('home');
  const tPromotions = useTranslations('promotions');
  const { playAudio } = useAudioHelper({ id: AudioId.ButtonClick });
  const { audioManager, playSound } = useAudioManager();
  const [selectedCurrency, setSelectedCurrency] = useAtom(selectedCurrencyAtom);
  const balanceList = useAtomValue(balanceListAtom);
  const setBalanceListData = useSetAtom(setBalanceListAtom);
  const clearRechargeNotification = useSetAtom(rechargeNotificationAtom);
  const user = useAtomValue(userAtom);
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();
  const balanceRef = useRef<HTMLDivElement>(null);
  const prevNotification = useRef<RechargeNotification | null>(null);
  const prevBalanceListRef = useRef<typeof balanceListData | null>(null);
  // 保存触发动画时的余额数据，确保动画完成后使用正确的数据
  const pendingBalanceListRef = useRef<typeof balanceListData | null>(null);
  const animationKeyRef = useRef(0);
  const [showRechargeAnimation, setShowRechargeAnimation] = useState(false);
  const [rechargeAnimationData, setRechargeAnimationData] = useState<{
    currency: string;
    amount: string;
  } | null>(null);
  // 用于控制显示的余额列表（动画期间显示旧余额，动画完成后显示新余额）
  const [displayBalanceList, setDisplayBalanceList] = useState<typeof balanceListData | null>(null);
  // 是否在rummy中
  const isRummyActive = useAtomValue(isRummyActiveAtom);
  // 判断是否进入了游戏：rummy游戏
  const isRummyInGame = useAtomValue(isRummyInGameAtom);
  const isCasinoInGame = useAtomValue(isCasinoInGameAtom);
  const isInKbFruitGame = useAtomValue(isInKbFruitGameAotm);
  // 是否在fantasy游戏中
  const isFantasyActive = useAtomValue(isFantasyActiveAtom);
  // sports
  const isSportsActive = useAtomValue(isSportsActiveAtom);

  // 获取余额数据（改为手动触发模式）
  const pollingPaused = useAtomValue(balancePollingPausedAtom);
  const { data: balanceListData, refetch } = useGetBalanceList({
    params: {},
    queryConfig: {
      enabled: false, // 改为手动触发
    },
  });

  // 初始加载
  useEffect(() => {
    if (user?.accessToken) {
      refetch();
    }
  }, [user?.accessToken, refetch]);

  // 订阅 balance 变化（通过统一的 change-check 轮询）
  const shouldSubscribe = !!user?.accessToken && !pollingPaused;
  useChangeSubscription('balance', refetch, shouldSubscribe);

  // 获取静态资源数据（需要在 useEffect 之前定义，因为 useEffect 中需要使用）
  const { data: staticResource } = useGetStaticResource();
  const getCurrencyIcon = useGetCurrencyIcon();
  const formatBalanceDisplay = useFormatBalanceDisplay();

  // 注册并预加载音频资源
  useEffect(() => {
    audioManager.soundEffectManager.register('balance-number-up', '/assets/audio-files/coin-progressbar.m4a');
    setTimeout(() => {
      audioManager.soundEffectManager.preload('balance-number-up');
    }, 300);
  }, [audioManager]);

  // 监听数据变化并更新 atom
  useEffect(() => {
    if (balanceListData) {
      setBalanceListData(balanceListData);
    }
  }, [balanceListData, setBalanceListData]);

  // 监听充值通知 atom - 只触发 refetch，不设置动画
  const rechargeNotification = useAtomValue(rechargeNotificationAtom);
  useEffect(() => {
    if (
      rechargeNotification &&
      rechargeNotification !== prevNotification.current &&
      rechargeNotification.eventType === 2 && // 只有充值才触发
      rechargeNotification.eventStatus === 1 // 只有成功才触发
    ) {
      // 记录当前通知
      prevNotification.current = rechargeNotification;
      // 触发 refetch，但不设置动画
      refetch();
    }
  }, [rechargeNotification, refetch]);

  // 监听余额数据变化，对比新旧余额，发现增加时触发动画
  useEffect(() => {
    // 如果动画正在显示，忽略新的余额更新，等待动画完成
    if (showRechargeAnimation) {
      return;
    }

    if (!balanceListData || !prevBalanceListRef.current) {
      // 首次加载或没有旧数据，直接更新显示
      if (balanceListData) {
        prevBalanceListRef.current = balanceListData;
        setDisplayBalanceList(balanceListData);
      }
      return;
    }

    const prevList = prevBalanceListRef.current.balance_list || [];
    const newList = balanceListData.balance_list || [];

    // 对比每个货币的余额变化，找出增加的货币
    let foundIncrease = false;
    let increasedCurrency = '';
    let increasedAmount = '0';

    for (const newItem of newList) {
      const prevItem = prevList.find(item => item.currency === newItem.currency);
      if (prevItem) {
        // 计算 balance 和 bonus 的总和
        const prevBalance = toNum(prevItem.balance || '0');
        const prevBonus = toNum(prevItem.bonus || '0');
        const prevTotal = add(prevBalance, prevBonus);

        const newBalance = toNum(newItem.balance || '0');
        const newBonus = toNum(newItem.bonus || '0');
        const newTotal = add(newBalance, newBonus);

        // 根据货币类型确定精度：法币 2 位小数，数字货币 4 位小数
        const currencyInfo = staticResource?.currency?.[newItem.currency as keyof typeof staticResource.currency] as any;
        const isFiat = Boolean(currencyInfo?.region);
        const decimalPlaces = isFiat ? 2 : 4;

        // 先将新旧总和分别格式化为显示精度，再计算差值
        // 这样可以避免小变化被忽略（例如：0.001 的变化在格式化后可能变成 0.01）
        const formattedPrevTotal = toDP(prevTotal, decimalPlaces, ROUND_DOWN);
        const formattedNewTotal = toDP(newTotal, decimalPlaces, ROUND_DOWN);
        const totalDiff = sub(formattedNewTotal, formattedPrevTotal);
        // console.log('totalDiff', totalDiff);

        // 如果总和增加了，触发动画
        if (totalDiff > 0) {
          foundIncrease = true;
          increasedCurrency = newItem.currency;
          // 动画显示的数值是总和增加的量
          increasedAmount = totalDiff.toString();
          break; // 只处理第一个增加的货币
        }
      }
      // 新货币不触发动画，因为首次加载时所有货币都是"新货币"
      // 只有在已有货币的 balance 和 bonus 总和增加时才触发动画
    }

    if (foundIncrease && !isInPlay) {
      // 有余额增加，触发动画
      // 保存触发动画时的余额数据
      pendingBalanceListRef.current = balanceListData;
      animationKeyRef.current += 1;
      setRechargeAnimationData({
        currency: increasedCurrency,
        amount: increasedAmount,
      });
      setShowRechargeAnimation(true);
      // 保持显示旧余额，等待动画完成
      // displayBalanceList 保持不变，prevBalanceListRef.current 也不更新
    } else {
      // 没有余额增加，直接更新显示
      setDisplayBalanceList(balanceListData);
      prevBalanceListRef.current = balanceListData;
    }
  }, [balanceListData, showRechargeAnimation, staticResource]);

  // 处理动效完成
  const handleAnimationComplete = useCallback(() => {
    // 动画完成后，更新显示的余额
    // 优先使用最新的 balanceListData（可能比触发动画时的数据更新）
    // 如果没有最新的，则使用触发动画时保存的数据
    const balanceDataToApply = balanceListData || pendingBalanceListRef.current;
    if (balanceDataToApply) {
      setDisplayBalanceList(balanceDataToApply);
      prevBalanceListRef.current = balanceDataToApply;
      pendingBalanceListRef.current = null;
      // 播放余额增加音效
      playSound('balance-number-up');
    }

    // 清空动画状态和动画数据
    setShowRechargeAnimation(false);
    setRechargeAnimationData(null);
    clearRechargeNotification(null);
    // 重置 prevNotification，允许下次再次触发
    prevNotification.current = null;

    // 注意：清空 showRechargeAnimation 后会触发 useEffect 重新运行
    // useEffect 会对比最新的 prevBalanceListRef.current 和 balanceListData
    // 如果发现有新的余额增加，会再次触发动画
  }, [balanceListData, clearRechargeNotification, playSound]);

  // 根据 selectedCurrency 从 balanceList 中找到对应的余额信息
  // 使用 displayBalanceList 或 balanceList（优先使用 displayBalanceList，如果没有则使用 balanceList）
  const displayList = displayBalanceList || balanceList;
  const currentBalanceInfo = displayList?.balance_list?.find(item => item.currency === selectedCurrency);
  // 获取当前币种的图标
  const currencyIcon = getCurrencyIcon(selectedCurrency, staticResource);

  // 判断是否在游戏中
  const isInPlay = useMemo(() => {
    const currentUrl = pathname;
    // rummy和fantasy特殊处理
    if (isRummyActive) {
      return true;
    }
    if (inPlayPages.some(str => currentUrl.includes(str))) {
      return true;
    }
    return false;
  }, [pathname, isRummyActive]);

  // 处理钱包图标点击
  const handleWalletClick = useCallback(() => {
    if (!user?.accessToken) {
      return;
    }
    playAudio();
    // 游戏中的充值来源，需要传递给钱包
    const depositType = isRummyActive ? DEPOSIT_TYPE_MAP.rummy : isInKbFruitGame ? DEPOSIT_TYPE_MAP.casual : '';
    // 游戏相关页面和游戏中，需要直接跳转到对应的入口
    const entrance = isInPlay ? CURRENCY_MAP_ENTRANCE[currentBalanceInfo?.currency as keyof typeof CURRENCY_MAP_ENTRANCE] || 'root' : 'root';
    showWalletModal({
      entrance: entrance as WalletEntrance,
      hideWithdraw: !!depositType,
      customParams: { source_type: depositType || '' }
    });
    // 弹出钱包页面时上报
    track('wallet_page_view', {
      source: 'Top entrance',
    });
  }, [isRummyActive, isInKbFruitGame, user?.accessToken, currentBalanceInfo, isInPlay]);

  // 判断是否正在加载
  const isLoading = !currentBalanceInfo && (!balanceList?.balance_list || balanceList.balance_list.length === 0);

  const handleCurrencySelect = async (currencyCode: string) => {
    const selectCurrency = () => {
      setSelectedCurrency(currencyCode);
    };
    if (currencyCode !== selectedCurrency) {
      if (isCasinoInGame || isRummyInGame || isRummyActive || isFantasyActive || isInKbFruitGame || isSportsActive) {
        const result = await showModalSecondConfirm({});
        if (result.result) {
          selectCurrency();
        }
        return;
      }
      selectCurrency();
    }
    setIsOpen(false);
  };

  const dropdownHeight = useMemo(() => {
    const len = displayList?.balance_list?.length || 0;
    if (len === 0) {
      return 'auto';
    }
    return `${len * 50 + 12 - 5}px`;
  }, [displayList?.balance_list]);

  useEffect(() => {
    const onBlur = () => {
      if (document.activeElement?.tagName === 'IFRAME') {
        setIsOpen(false);
      }
    };
    window.addEventListener('blur', onBlur);

    return () => {
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  useEffect(() => {
    const handleDocument = (event) => {
      const target = event.target;
      const parents = getParents(target);
      if (target.classList?.contains('radix_dropdown_menu_trigger') ||
        target.classList?.contains('radix_dropdown_menu_content')) {
        return;
      }
      if (parents.filter(node => node.classList?.contains('radix_dropdown_menu_trigger') ||
        node.classList?.contains('radix_dropdown_menu_content')).length === 0) {
        setIsOpen(false);
      }
    };

    const eventName = isTouchSupported() ? 'touchstart' : 'mousedown';
    document.addEventListener(eventName, handleDocument);

    return () => {
      document.removeEventListener(eventName, handleDocument);
    };
  }, []);

  return (
    <div className="w-[161px] h-[35px] relative ml-[6px] flex items-center mt-[2px]">
      {isLoading ? (
        <div className="bg-black/35 animate-pulse rounded-[8px] relative w-full h-[30px] flex items-center pl-[5px] cursor-pointer">
          <div className="w-5 h-5 bg-gray-600 rounded-[50%]"></div>
          <div className="flex-1 text-white text-s font-semibold ml-1.5 whitespace-nowrap mr-[6px]">0</div>
          <IconFont
            name="arrow-down"
            size={7}
            color="#a9b5cb"
            className="mr-[53px] transition-transform duration-200"
          />
        </div>
      ) : (
        <DropdownMenu.Root
          open={isOpen}
          onOpenChange={(open) => {
            playAudio();
            setIsOpen(open);
          }}
          modal={false}>
          <DropdownMenu.Trigger asChild>
            <div className="radix_dropdown_menu_trigger bg-black/35 rounded-[8px] relative w-full h-[30px] flex items-center pl-[5px] cursor-pointer">
              {
                isInPlay && <div className="pr-[5px] text-s text-white whitespace-nowrap font-medium">{t('inPlay')}</div>
              }
              {currencyIcon && (
                <Image
                  src={currencyIcon}
                  alt={selectedCurrency}
                  className='flex-none'
                  width={20}
                  height={20}
                  priority
                />
              )}
              <div ref={balanceRef} className="flex-1 text-white text-s font-semibold whitespace-nowrap ml-[4px] mr-[5px]">
                {!isInPlay && formatBalanceDisplay({
                  value: toDP(add(
                    toNum(currentBalanceInfo?.balance || '0'),
                    toNum(currentBalanceInfo?.bonus || '0')),
                    4, ROUND_DOWN) as number,
                  defaultValue: add(
                    toNum(currentBalanceInfo?.balance || '0'),
                    toNum(currentBalanceInfo?.bonus || '0')),
                  currency: selectedCurrency as string
                })}
              </div>
              <IconFont name="arrow-down" size={7} color='#a9b5cb' className={cn("mr-[53px] transition-transform duration-200", isOpen && "rotate-180")} />
            </div>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className="w-[208px] z-50 radix_dropdown_menu_content"
              style={{ boxShadow: '0px 0px 5px rgba(0, 0, 0, 0.3)' }}
              sideOffset={12}
              align="center"
              onFocusOutside={(e) => {
                const target = e.target as HTMLElement;
                // 如果焦点转移到了 vaul drawer 内部，则阻止 DropdownMenu 关闭
                if (target.closest('[data-vaul-drawer-direction]')) {
                  e.preventDefault();
                }
              }}
              onPointerDownOutside={(e) => {
                const target = e.target as HTMLElement;
                // 如果点击发生在 vaul drawer 内部，则阻止 DropdownMenu 关闭
                if (target.closest('[data-vaul-drawer-direction]')) {
                  e.preventDefault();
                }
              }}
            >
              <div
                className="flex justify-center w-[208px] overflow-hidden"
                style={{ height: dropdownHeight }}
              >
                <div
                  className={`w-full rounded-[10px] p-[6px] bg-surface ${styles.balanceDropdownContentAnim}`}
                  style={{ boxShadow: '0px 0px 5px rgba(0, 0, 0, 0.3)' }}
                >
                  {displayList?.balance_list?.map((balanceItem, index) => {
                    const isSelected = balanceItem.currency === selectedCurrency;

                    return (
                      <div key={balanceItem.currency} className={`relative bg-surface mb-[5px] last:mb-0 ${styles.balanceDropdownItemAnim}`} style={{
                        animationDelay: `${0.08 * index + 0.13}s`,
                        zIndex: 20 - index,
                      }}>
                        <BalanceItem
                          currency={balanceItem.currency}
                          balance={balanceItem.balance || '0'}
                          bonus={balanceItem.bonus || '0'}
                          isSelected={isSelected}
                          onSelectAction={() => handleCurrencySelect(balanceItem.currency)}
                          getCurrencyIcon={getCurrencyIcon}
                          t={tPromotions}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      )}

      <Image
        className="absolute right-0 top-0 cursor-pointer active:scale-90"
        src={walletIcon}
        alt="wallet"
        width={44}
        height={35}
        onClick={handleWalletClick}
        style={{ height: 35 }}
        priority
      />

      {/* 充值成功动效 */}
      {showRechargeAnimation && rechargeAnimationData && (
        <RechargeAnimation
          key={`recharge-${animationKeyRef.current}`}
          currency={rechargeAnimationData.currency}
          amount={rechargeAnimationData.amount}
          onAnimationCompleteAction={handleAnimationComplete}
        />
      )}
    </div>
  );
}
